import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Play, Pause, Volume2, Music, ArrowRight, Star, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PreviewPage() {
  const { creativeId } = useParams();
  const navigate = useNavigate();
  const [creative, setCreative] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const resp = await fetch(`${API}/preview/${creativeId}`);
        if (!resp.ok) throw new Error('Creative not found');
        const data = await resp.json();
        setCreative(data);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPreview();
  }, [creativeId]);

  const handleCtaClick = async () => {
    try {
      await fetch(`${API}/preview/${creativeId}/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target: 'cta', referrer: window.location.href }),
      });
    } catch {}
    navigate(`/auth?ref=preview&creative=${creativeId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0b0f] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error || !creative) {
    return (
      <div className="min-h-screen bg-[#0a0b0f] flex items-center justify-center">
        <div className="text-center">
          <p className="text-zinc-400 text-lg mb-4">{error || 'Creative not found'}</p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0b0f]" data-testid="preview-page">
      {/* Header */}
      <div className="border-b border-zinc-800/50 bg-zinc-900/30 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
              <span className="text-white text-xs font-black">D</span>
            </div>
            <span className="text-sm font-semibold text-zinc-300">Delphi AI</span>
          </div>
          <Button size="sm" onClick={handleCtaClick} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="preview-cta-top">
            Create Your Own <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Score badge */}
        {creative.overall_score > 0 && (
          <div className="flex items-center gap-2 mb-4">
            <div className="flex items-center gap-1 px-3 py-1 bg-orange-500/10 border border-orange-500/20 rounded-full">
              <Star className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-sm font-bold text-orange-400">Score: {creative.overall_score}</span>
            </div>
            <span className="text-xs text-zinc-500">{creative.framework_label} · {creative.platform}</span>
          </div>
        )}

        {/* Hook Headline */}
        <h1 className="text-2xl sm:text-3xl font-bold text-zinc-100 mb-6 leading-tight" data-testid="preview-hook">
          {creative.hook}
        </h1>

        {/* Video Player */}
        {creative.video_url && (
          <div className="mb-6 rounded-2xl overflow-hidden bg-zinc-900 border border-zinc-800" data-testid="preview-video">
            <video
              controls
              className="w-full aspect-video"
              src={`${BACKEND_URL}${creative.video_url}`}
              poster=""
              preload="metadata"
            />
          </div>
        )}

        {/* Voice Narration */}
        {creative.voice_url && (
          <div className="mb-6 p-4 bg-zinc-900/60 border border-zinc-800 rounded-xl" data-testid="preview-voice">
            <p className="text-xs text-zinc-500 mb-2 flex items-center gap-1"><Volume2 className="w-3 h-3" /> Voice Narration</p>
            <audio controls src={`${BACKEND_URL}${creative.voice_url}`} className="w-full" preload="none" />
          </div>
        )}

        {/* Script */}
        <div className="mb-6 p-5 bg-zinc-900/60 border border-zinc-800 rounded-xl" data-testid="preview-script">
          <p className="text-xs text-zinc-500 mb-2 uppercase tracking-wider font-medium">Script</p>
          <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-line">{creative.script}</p>
        </div>

        {/* Caption */}
        {creative.caption && (
          <div className="mb-6 p-5 bg-zinc-900/60 border border-zinc-800 rounded-xl" data-testid="preview-caption">
            <p className="text-xs text-zinc-500 mb-2 uppercase tracking-wider font-medium">Caption</p>
            <p className="text-zinc-300 text-sm leading-relaxed">{creative.caption}</p>
          </div>
        )}

        {/* Music */}
        {creative.music_url && (
          <div className="mb-6 p-4 bg-zinc-900/60 border border-zinc-800 rounded-xl" data-testid="preview-music">
            <p className="text-xs text-zinc-500 mb-2 flex items-center gap-1"><Music className="w-3 h-3" /> Soundtrack</p>
            <audio controls src={`${BACKEND_URL}${creative.music_url}`} className="w-full" preload="none" />
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-10 p-6 bg-gradient-to-br from-orange-500/10 to-red-500/5 border border-orange-500/20 rounded-2xl text-center" data-testid="preview-cta-section">
          <p className="text-lg font-bold text-zinc-100 mb-2">Create ads like this in 60 seconds.</p>
          <p className="text-sm text-zinc-400 mb-4">AI-powered campaign generation with hooks, scripts, voice, and music.</p>
          <Button size="lg" onClick={handleCtaClick} className="bg-orange-500 hover:bg-orange-600 text-white px-8" data-testid="preview-cta-main">
            Start your free trial <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-zinc-600">Created with <span className="text-orange-500 font-medium">Delphi AI</span></p>
        </div>
      </div>
    </div>
  );
}
